<template>
    <div>
        <div class="text-nowrap">
            <h5>{{ approvalStatusLabel }} {{ approvalStatusText }}</h5>
        </div>
        <div class="mb-3">
            {{ approvalStatusDescriptionText }}
        </div>
        <div>
            <pc-select
                id="ApprovalStatusSelector"
                v-model="selectedStatusId"
                v-bind:label="setApprovalStatusLabel"
                v-bind:options="workflowStatuses"
                v-bind:text-prop="'Name'"
                v-bind:value-prop="'PublicId'"
                data-test-role="approval-status-selector">
                <option value="">
                    {{ noApprovalStatusOption }}
                </option>
            </pc-select>
        </div>
        <div class="d-flex flex-row justify-content-end mt-3">
            <pc-button
                id="InitSelectedStatusButton"
                class="me-2"
                v-bind:disabled="selectedStatusIsApprovalStatus"
                v-on:click="setSelectedStatusToApprovalStatus">
                {{ initSelectedStatusButtonText }}
            </pc-button>
            <pc-button
                id="SetApprovalStatusButton"
                primary
                v-bind:disabled="selectedStatusIsApprovalStatus"
                v-on:click="$emit('changeapprovalstatus', selectedStatusId, setSelectedStatusToApprovalStatus)">
                {{ setApprovalStatusButtonText }}
            </pc-button>
        </div>
        <hr />
        <div class="text-nowrap">
            <h5>{{ metadataSectionHeader }}</h5>
        </div>
        <div>{{ metadataCountLimitText }}</div>
        <br />
        <div>
            <div v-if="canAddMetadataField">
                <pc-button
                    id="AddMetadataFieldButton"
                    primary
                    v-on:click="$emit('addmetadata')">
                    {{ addMetadataFieldButtonText }}
                </pc-button>
                <br />
            </div>
            <br />
            <pc-table
                id="IncentiveMetadataTable"
                v-bind:items="metadataColumns"
                v-bind:columns="columns"
                v-bind:items-per-page="13"
                v-bind:empty-text="emptyMetadataTableText"
                key-prop="PublicId"
                hide-search
                save-state
                state-key="incentive-metadata-list">
                <template v-slot:DataFieldTypeConstantId="metadata">
                    {{ getMetadataTypeText(metadata) }}
                </template>
                <template v-slot:actionsRow="row">
                    <div class="d-flex">
                        <pc-button
                            id="EditMetadataFieldButton"
                            class="mt-1"
                            data-test-role="rename-metadata-field"
                            v-on:click="$emit('editmetadata', row.PublicId)">
                            {{ editMetadataButtonText }}
                        </pc-button>
                        <pc-button
                            id="DeleteMetadataFieldButton"
                            link
                            class="ms-auto"
                            data-test-role="delete-metadata-field"
                            v-on:click="$emit('deletemetadata', row.PublicId)">
                            {{ deleteMetadataButtonText }}
                        </pc-button>
                    </div>
                </template>
            </pc-table>
        </div>
    </div>
</template>

<script>
    import { KEYS, NAMESPACE } from "./i18n/incentiveOptionsTab.i18n";
    import { mapI18n } from "Util/i18n";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";

    import { MAX_INCENTIVE_METADATA_FIELDS, METADATA_TYPE } from "./incentiveDesignList.vue";

    export default {
        name: "IncentiveOptionsTab",
        props: {
            workflowStatuses: { type: Array, required: true },
            approvalStatusId: { type: String, required: true },
            metadataColumns: { type: Array, required: true }
        },
        emits: ["changeapprovalstatus", "addmetadata", "editmetadata", "deletemetadata"],
        data() {
            return {
                selectedStatusId: ""
            };
        },
        computed: {
            columns() {
                return [
                    {
                        header: "Order",
                        property: "OrderNumber",
                        orderable: true,
                        visible: true
                    },
                    {
                        header: "Name",
                        property: "SchemaRowName",
                        orderable: true,
                        visible: true
                    },
                    {
                        header: "Data Type",
                        property: "DataFieldTypeConstantId",
                        orderable: true,
                        visible: true
                    },
                    getClientTimestampTableColumn("Last Changed", "LastChangeTimestamp"),
                    {
                        header: "PublicId",
                        property: "PublicId",
                        orderable: false,
                        visible: false
                    }
                ];
            },
            canAddMetadataField() {
                return this.metadataColumns.length < MAX_INCENTIVE_METADATA_FIELDS;
            },
            approvalStatus() {
                if (!this.workflowStatuses) {
                    return null;
                }
                return this.workflowStatuses.find((s) => s.PublicId === this.approvalStatusId);
            },
            approvalStatusText() {
                if (this.approvalStatus) {
                    return this.approvalStatus.Name;
                }
                return this.noSelectedApprovalStatusText;
            },
            selectedStatusIsApprovalStatus() {
                return this.approvalStatusId === this.selectedStatusId;
            },
            //Support for localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            approvalStatusId() {
                this.setSelectedStatusToApprovalStatus();
            }
        },
        mounted() {
            this.setSelectedStatusToApprovalStatus();
        },
        methods: {
            setSelectedStatusToApprovalStatus() {
                this.selectedStatusId = this.approvalStatusId;
            },
            getMetadataTypeText(metadata) {
                switch (metadata.DataFieldTypeConstantId) {
                    case METADATA_TYPE.Text:
                        return "Text";
                    case METADATA_TYPE.Decimal:
                        return "Decimal";
                }
                return "";
            }
        }
    };
</script>
